<template>
  <div class="uplimit-multi-ladder-view">
    <!-- 控制栏 -->
    <div class="row align-items-center mb-2 px-2">
      <div class="d-flex flex-wrap align-items-center w-100 ladder-toolbar">
        <!-- 截止日期 -->
        <div class="control-item">
          <b-form-datepicker 
            id="ladder-datepicker" 
            size="sm" 
            :date-disabled-fn="dateDisabled" 
            v-model="end_date" 
            today-button 
            close-button 
            locale="zh"
          ></b-form-datepicker>
        </div>

        <!-- 天数选择 -->
        <div class="control-item">
          <b-form-select v-model="days" :options="dayOptions" size="sm"></b-form-select>
        </div>

        <!-- 最低板数 -->
        <div class="control-item">
          <b-form-select v-model="min_ban" :options="minBanOptions" size="sm"></b-form-select>
        </div>

        <!-- 行情开关 -->
        <div class="control-item">
          <b-form-checkbox id="cb_ladder_market" v-model.number="status_market" :value="1" :unchecked-value="0" size="sm">行情</b-form-checkbox>
        </div>

        <!-- 显隐配置 -->
        <div class="control-item">
          <b-form-checkbox id="cb_ladder_time" v-model.number="show_time" :value="1" :unchecked-value="0" size="sm">时间</b-form-checkbox>
        </div>
        <div class="control-item">
          <b-form-checkbox id="cb_ladder_plate" v-model.number="show_plate" :value="1" :unchecked-value="0" size="sm">题材</b-form-checkbox>
        </div>
        <div class="control-item">
          <b-form-checkbox id="cb_ladder_count" v-model.number="show_count" :value="1" :unchecked-value="0" size="sm">个数</b-form-checkbox>
        </div>
        <div class="control-item">
          <b-form-checkbox id="cb_ladder_amount" v-model.number="show_amount" :value="1" :unchecked-value="0" size="sm">成交额</b-form-checkbox>
        </div>
        <div class="control-item">
          <b-form-checkbox id="cb_ladder_market_value" v-model.number="show_market_value" :value="1" :unchecked-value="0" size="sm">市值</b-form-checkbox>
        </div>
        <div class="control-item">
          <b-form-checkbox id="cb_ladder_sealed" v-model.number="show_sealed_order" :value="1" :unchecked-value="0" size="sm">封单</b-form-checkbox>
        </div>
        <div class="control-item">
          <b-form-checkbox id="cb_ladder_highlight" v-model.number="highlight_same_stock" :value="1" :unchecked-value="0" size="sm">同股追踪</b-form-checkbox>
        </div>

        <!-- 刷新按钮 -->
        <div class="control-item ml-auto">
          <b-button size="sm" variant="primary" :disabled="loading" @click="fetchLadderData">
            <b-spinner small v-if="loading" class="mr-1"></b-spinner>
            <span>刷新</span>
          </b-button>
        </div>
      </div>
    </div>

    <!-- 加载中遮罩 -->
    <div v-if="loading && dates.length === 0" class="text-center py-5">
      <b-spinner variant="primary" label="加载中..."></b-spinner>
      <div class="text-muted mt-2 small">正在组装多日连板天梯数据...</div>
    </div>

    <!-- 多日天梯矩阵容器：支持横向与纵向平滑滚动 -->
    <div v-else class="ladder-scroll-wrapper" ref="scrollContainer">
      <div class="ladder-grid" :style="{ minWidth: (dates.length * 155 + 95) + 'px' }">
        <!-- 头部日期行 -->
        <div class="ladder-row ladder-header-row no-gutters flex-nowrap">
          <!-- 左上角固定角标 -->
          <div class="ladder-header-col corner-col border d-flex flex-column align-items-center justify-content-center text-center">
            <span class="corner-title">梯队 / 日期</span>
            <span class="corner-sub">{{ dates.length }}个交易日</span>
          </div>

          <!-- 各交易日列头 (最新列在最前面/最左侧) -->
          <div 
            class="ladder-day-col border text-center d-flex flex-column justify-content-center py-1 px-1"
            v-for="(dateStr, idx) in dates" 
            :key="'header-' + dateStr"
            :class="{ 'latest-col-header': idx === 0 }"
          >
            <div class="date-header-top d-flex align-items-center justify-content-center">
              <span class="date-short">{{ formatDateShort(dateStr) }}</span>
              <span class="weekday-badge ml-1">{{ getWeekday(dateStr) }}</span>
              <span v-if="idx === 0" class="latest-tag ml-1">最新</span>
            </div>
            <div class="date-header-bottom text-muted small mt-1" v-if="show_count">
              <span>{{ getDateStockCount(dateStr) }}只</span>
              <span class="dot-sep">·</span>
              <span class="max-ban-text">最高{{ getDateMaxBan(dateStr) }}板</span>
            </div>
          </div>
        </div>

        <!-- 连板高度行 (从最高板降序到最低连板，7板及以上合并为一行) -->
        <div 
          class="ladder-row no-gutters flex-nowrap" 
          v-for="ban in displayBans" 
          :key="'ban-' + ban"
        >
          <!-- 左侧连板层级标签 -->
          <div class="ladder-header-col tier-col border d-flex flex-column align-items-center justify-content-center py-1">
            <div class="ban-title-wrap">
              <span class="ban-number" :style="getBanBadgeStyle(ban)">{{ formatBanTitle(ban) }}</span>
              <span v-if="show_count && getBanLatestCount(ban) > 0" class="latest-ban-count ml-1">
                {{ getBanLatestCount(ban) }}只
              </span>
            </div>
            <span class="ban-total-hint small" v-if="show_count">
              区间{{ getBanTotalCount(ban) }}只次
            </span>
          </div>

          <!-- 对应各交易日的格子 -->
          <div 
            class="ladder-day-col border ladder-content-cell p-1"
            v-for="(dateStr, dIdx) in dates" 
            :key="'cell-' + ban + '-' + dateStr"
            :class="{ 'latest-col-cell': dIdx === 0, 'empty-cell': !hasStocks(ban, dateStr) }"
          >
            <!-- 股票列表 -->
            <div 
              class="stock-item-card mb-1" 
              v-for="stock in getStocks(ban, dateStr)" 
              :key="stock.stock_code + '_' + dateStr + '_' + ban"
              :class="{ 
                'same-stock-highlight': highlight_same_stock && hoveredStockCode === stock.stock_code,
                'stock-promoted': stock.next_open_pct && stock.next_open_pct > 0
              }"
              @mouseenter="onStockHover(stock.stock_code)"
              @mouseleave="onStockLeave"
              @click="onStockClick(stock)"
              v-b-toggle.sidebar-right-stock
              v-b-popover.hover.top="getStockPopover(stock)"
            >
              <!-- 首行：时间 + 名称 + 连板标记 + 市场标 + 行情 -->
              <div class="stock-card-top d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center text-truncate">
                  <span v-if="show_time && stock.up_limit_time" class="stock-time mr-1">
                    {{ stock.up_limit_time }}
                  </span>
                  <span class="stock-name font-weight-bold">{{ stock.stock_name }}</span>
                  <!-- 仅大于等于7的这一行在股票后面显示具体几板信息 -->
                  <span 
                    v-if="ban === '≥7' || ban === '7+'" 
                    class="stock-ban-pill ml-1 ban-pill-super"
                  >
                    {{ stock.up_limit_keep_times }}板
                  </span>
                  <span v-if="stock.stock_code.startsWith('30')" class="circle-badge circle-c ml-1">ⓒ</span>
                  <span v-else-if="stock.stock_code.startsWith('68')" class="circle-badge circle-k ml-1">Ⓚ</span>
                  <span v-else-if="stock.stock_code.startsWith('8') || stock.stock_code.startsWith('9')" class="circle-badge circle-b ml-1">Ⓑ</span>
                </div>
                <!-- 实时行情涨跌幅 -->
                <div v-if="status_market && realQuotes[stock.stock_code]" class="stock-real-rate ml-1" :style="getQuoteStyle(realQuotes[stock.stock_code])">
                  {{ realQuotes[stock.stock_code].px_change_rate }}%
                </div>
              </div>

              <!-- 次行：题材概念标签 -->
              <div v-if="show_plate && stock.plates && stock.plates.length > 0" class="stock-card-plates mt-1">
                <span 
                  class="plate-badge" 
                  v-for="(p, pIdx) in stock.plates.slice(0, 2)" 
                  :key="pIdx"
                >{{ p }}</span>
                <span v-if="stock.plates.length > 2" class="plate-more">+{{ stock.plates.length - 2 }}</span>
              </div>

              <!-- 附加指标行 (成交额 / 市值 / 封单) -->
              <div v-if="show_amount || show_market_value || show_sealed_order" class="stock-card-metrics mt-1 text-muted small">
                <span v-if="show_amount && stock.amount" class="metric-item mr-1">
                  {{ stock.amount }}亿
                </span>
                <span v-if="show_market_value && stock.market_c_c" class="metric-item mr-1">
                  {{ stock.market_c_c }}亿
                </span>
                <span v-if="show_sealed_order && stock.fd_close" class="metric-item fd-item">
                  封{{ stock.fd_close }}亿
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { request_zzquant } from 'network/request.js'

export default {
  name: 'UplimitMultiDayLadder',
  data() {
    return {
      reqId: 0,
      loading: false,
      end_date: '',
      days: 12, // 默认 12 天
      min_ban: 2, // 默认 >= 2板
      dayOptions: [
        { value: 5, text: '最近 5 天' },
        { value: 8, text: '最近 8 天' },
        { value: 10, text: '最近 10 天' },
        { value: 12, text: '最近 12 天 (默认)' },
        { value: 15, text: '最近 15 天' },
        { value: 20, text: '最近 20 天' },
        { value: 30, text: '最近 30 天' }
      ],
      minBanOptions: [
        { value: 2, text: '>= 2板 (默认)' },
        { value: 3, text: '>= 3板' },
        { value: 4, text: '>= 4板' },
        { value: 1, text: '>= 1板 (含首板)' }
      ],
      status_market: 0,
      show_time: 1,
      show_plate: 1,
      show_count: 1,
      show_amount: 0,
      show_market_value: 0,
      show_sealed_order: 0,
      highlight_same_stock: 1,
      hoveredStockCode: '',
      // 后端返回的数据
      dates: [],
      max_ban: 2,
      bans: [],
      date_info: {},
      ladder: {},
      all_stocks: [],
      realQuotes: {},
      all_trading_days: []
    }
  },
  mounted() {
    // 从 localStorage 恢复用户习惯
    const savedDays = localStorage.getItem('uplimit_ladder_days')
    const savedMinBan = localStorage.getItem('uplimit_ladder_min_ban')
    const savedStatusMarket = localStorage.getItem('uplimit_ladder_status_market')
    const savedShowTime = localStorage.getItem('uplimit_ladder_show_time')
    const savedShowPlate = localStorage.getItem('uplimit_ladder_show_plate')
    const savedShowCount = localStorage.getItem('uplimit_ladder_show_count')
    const savedShowAmount = localStorage.getItem('uplimit_ladder_show_amount')
    const savedShowMarketValue = localStorage.getItem('uplimit_ladder_show_market_value')
    const savedShowSealedOrder = localStorage.getItem('uplimit_ladder_show_sealed_order')
    const savedHighlight = localStorage.getItem('uplimit_ladder_highlight_same_stock')

    if (savedDays !== null) this.days = parseInt(savedDays)
    if (savedMinBan !== null) this.min_ban = parseInt(savedMinBan)
    if (savedStatusMarket !== null) this.status_market = parseInt(savedStatusMarket)
    if (savedShowTime !== null) this.show_time = parseInt(savedShowTime)
    if (savedShowPlate !== null) this.show_plate = parseInt(savedShowPlate)
    if (savedShowCount !== null) this.show_count = parseInt(savedShowCount)
    if (savedShowAmount !== null) this.show_amount = parseInt(savedShowAmount)
    if (savedShowMarketValue !== null) this.show_market_value = parseInt(savedShowMarketValue)
    if (savedShowSealedOrder !== null) this.show_sealed_order = parseInt(savedShowSealedOrder)
    if (savedHighlight !== null) this.highlight_same_stock = parseInt(savedHighlight)

    // 查询最新交易日
    request_zzquant({ url: '/v3/market/trade/days' }).then(res => {
      const days = res && res.data && res.data.data ? res.data.data : []
      if (days && days.length > 0) {
        this.all_trading_days = days
        this.end_date = days[days.length - 1]
      } else {
        const now = new Date()
        const y = now.getFullYear()
        const m = String(now.getMonth() + 1).padStart(2, '0')
        const d = String(now.getDate()).padStart(2, '0')
        this.end_date = `${y}-${m}-${d}`
      }
      this.fetchLadderData()
    }).catch(() => {
      const now = new Date()
      const y = now.getFullYear()
      const m = String(now.getMonth() + 1).padStart(2, '0')
      const d = String(now.getDate()).padStart(2, '0')
      this.end_date = `${y}-${m}-${d}`
      this.fetchLadderData()
    })
  },
  watch: {
    end_date(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.fetchLadderData()
      }
    },
    days(newVal) {
      localStorage.setItem('uplimit_ladder_days', newVal)
      this.fetchLadderData()
    },
    min_ban(newVal) {
      localStorage.setItem('uplimit_ladder_min_ban', newVal)
      this.fetchLadderData()
    },
    status_market(newVal) {
      localStorage.setItem('uplimit_ladder_status_market', newVal)
      if (newVal === 1 && this.all_stocks.length > 0) {
        this.fetchRealQuotes()
      }
    },
    show_time(newVal) {
      localStorage.setItem('uplimit_ladder_show_time', newVal)
    },
    show_plate(newVal) {
      localStorage.setItem('uplimit_ladder_show_plate', newVal)
    },
    show_count(newVal) {
      localStorage.setItem('uplimit_ladder_show_count', newVal)
    },
    show_amount(newVal) {
      localStorage.setItem('uplimit_ladder_show_amount', newVal)
    },
    show_market_value(newVal) {
      localStorage.setItem('uplimit_ladder_show_market_value', newVal)
    },
    show_sealed_order(newVal) {
      localStorage.setItem('uplimit_ladder_show_sealed_order', newVal)
    },
    highlight_same_stock(newVal) {
      localStorage.setItem('uplimit_ladder_highlight_same_stock', newVal)
    }
  },
  computed: {
    displayBans() {
      if (!this.bans || this.bans.length === 0) return []
      if (this.bans.includes('≥7')) return this.bans
      if (this.max_ban >= 7) {
        const lowerBans = this.bans.filter(b => Number(b) < 7)
        return ['≥7', ...lowerBans]
      }
      return this.bans
    }
  },
  methods: {
    formatBanTitle(ban) {
      if (ban === '≥7' || ban === '7+') return '≥7板'
      return ban + '板'
    },
    dateDisabled(ymd, date) {
      const weekday = date.getDay()
      if (weekday === 0 || weekday === 6) return true
      if (date.getTime() >= new Date().getTime()) return true
      return false
    },
    fetchLadderData() {
      if (!this.end_date) return
      const curReqId = ++this.reqId
      this.loading = true

      const url = `/v3/open/review/uplimit/ladder?end_date=${this.end_date}&days=${this.days}&min_ban=${this.min_ban}`
      request_zzquant({ url }).then(res => {
        if (curReqId !== this.reqId) return
        const data = res && res.data && res.data.data ? res.data.data : null
        if (data) {
          this.dates = data.dates || []
          this.max_ban = data.max_ban || 2
          this.bans = data.bans || []
          this.date_info = data.date_info || {}
          this.ladder = data.ladder || {}
          this.all_stocks = data.stocks || []

          // 如果开启了行情展示，拉取实时行情
          if (this.status_market === 1 && this.all_stocks.length > 0) {
            this.fetchRealQuotes()
          }
        }
        this.loading = false
      }).catch(err => {
        console.error('fetchLadderData error:', err)
        if (curReqId !== this.reqId) return
        this.loading = false
      })
    },
    fetchRealQuotes() {
      if (!this.all_stocks || this.all_stocks.length === 0) return
      const symbols = this.all_stocks.slice(0, 200).join(',')
      request_zzquant({ url: `/v3/open/market/real?symbols=${symbols}` }).then(res => {
        const d = res && res.data && res.data.data
        if (d && d.fields && d.values) {
          const quotes = {}
          const fields = d.fields
          const values = d.values
          for (let i = 0; i < fields.length; i++) {
            quotes[fields[i]] = values[i]
          }
          this.realQuotes = quotes
        }
      }).catch(err => {
        console.warn('fetchRealQuotes error:', err)
      })
    },
    getStocks(ban, dateStr) {
      const banKey = String(ban)
      if (ban === '≥7' || ban === '7+') {
        if (this.ladder['≥7'] && this.ladder['≥7'][dateStr]) {
          return this.ladder['≥7'][dateStr]
        }
        let list = []
        for (let b = this.max_ban; b >= 7; b--) {
          const k = String(b)
          if (this.ladder[k] && this.ladder[k][dateStr]) {
            list = list.concat(this.ladder[k][dateStr])
          }
        }
        list.sort((a, b) => (b.up_limit_keep_times - a.up_limit_keep_times) || (a.up_limit_time || '99:99').localeCompare(b.up_limit_time || '99:99'))
        return list
      }
      if (this.ladder[banKey] && this.ladder[banKey][dateStr]) {
        return this.ladder[banKey][dateStr]
      }
      return []
    },
    hasStocks(ban, dateStr) {
      return this.getStocks(ban, dateStr).length > 0
    },
    formatDateShort(dateStr) {
      if (!dateStr || dateStr.length < 10) return dateStr
      return dateStr.substring(5) // '10-21'
    },
    getWeekday(dateStr) {
      if (this.date_info[dateStr] && this.date_info[dateStr].weekday) {
        return this.date_info[dateStr].weekday
      }
      return ''
    },
    getDateStockCount(dateStr) {
      if (this.date_info[dateStr] && this.date_info[dateStr].stock_count !== undefined) {
        return this.date_info[dateStr].stock_count
      }
      return 0
    },
    getDateMaxBan(dateStr) {
      if (this.date_info[dateStr] && this.date_info[dateStr].max_ban !== undefined) {
        return this.date_info[dateStr].max_ban
      }
      return 0
    },
    getBanLatestCount(ban) {
      if (!this.dates || this.dates.length === 0) return 0
      const latestDate = this.dates[0]
      return this.getStocks(ban, latestDate).length
    },
    getBanTotalCount(ban) {
      let total = 0
      for (let i = 0; i < this.dates.length; i++) {
        total += this.getStocks(ban, this.dates[i]).length
      }
      return total
    },
    getBanBadgeStyle(ban) {
      // 随着连板数增高，色彩渐变递进
      if (ban === '≥7' || Number(ban) >= 7) {
        return { color: '#ef4444', fontWeight: '700' }
      } else if (Number(ban) >= 4) {
        return { color: '#f97316', fontWeight: '600' }
      } else if (Number(ban) >= 3) {
        return { color: '#eab308', fontWeight: '600' }
      }
      return { color: '#38bdf8', fontWeight: '500' }
    },
    getBanPillClass(keepTimes) {
      const b = Number(keepTimes)
      if (b >= 7) return 'ban-pill-super'
      if (b >= 4) return 'ban-pill-high'
      if (b >= 3) return 'ban-pill-mid'
      return 'ban-pill-normal'
    },
    getQuoteStyle(quote) {
      if (!quote || quote.px_change_rate === undefined) return {}
      const rate = Number(quote.px_change_rate)
      if (rate > 0) return { color: '#ef4444', fontWeight: '600' }
      if (rate < 0) return { color: '#22c55e', fontWeight: '600' }
      return { color: 'var(--text-muted)' }
    },
    onStockHover(code) {
      if (this.highlight_same_stock) {
        this.hoveredStockCode = code
      }
    },
    onStockLeave() {
      this.hoveredStockCode = ''
    },
    onStockClick(stock) {
      this.$emit('show_sidebar', {
        stock_code: stock.stock_code,
        stock_name: stock.stock_name,
        date1: stock.date1
      })
    },
    getStockPopover(stock) {
      const parts = [
        `【${stock.stock_name} (${stock.stock_code})】`,
        `日期: ${stock.date1}  连板: ${stock.up_limit_desc || stock.up_limit_keep_times + '板'}`,
        `涨停时间: ${stock.up_limit_time || '--'} [${stock.up_limit_type || '自然板'}]`
      ]
      if (stock.plates && stock.plates.length > 0) {
        parts.push(`所属概念: ${stock.plates.join(', ')}`)
      }
      if (stock.amount) parts.push(`成交额: ${stock.amount} 亿`)
      if (stock.market_c_c) parts.push(`流通市值: ${stock.market_c_c} 亿`)
      if (stock.fd_close) parts.push(`封单: ${stock.fd_close} 亿 (最大: ${stock.fd_max || '-'} 亿)`)
      return parts.join('\n')
    }
  }
}
</script>

<style scoped>
.uplimit-multi-ladder-view {
  overflow-x: hidden;
  padding-bottom: 24px;
}

.ladder-toolbar {
  gap: 8px;
}

.control-item {
  display: flex;
  align-items: center;
}

/* 横向与纵向平滑滚动容器 */
.ladder-scroll-wrapper {
  overflow-x: auto;
  overflow-y: visible;
  padding-bottom: 8px;
  background: var(--bg-card, #1e2638);
  border: 1px solid var(--border-color, #2d3748);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.ladder-scroll-wrapper::-webkit-scrollbar {
  height: 8px;
}

.ladder-scroll-wrapper::-webkit-scrollbar-track {
  background: var(--bg-muted, #171d2b);
  border-radius: 4px;
}

.ladder-scroll-wrapper::-webkit-scrollbar-thumb {
  background: var(--border-color, #3b475d);
  border-radius: 4px;
}

.ladder-scroll-wrapper::-webkit-scrollbar-thumb:hover {
  background: var(--accent-color, #3b82f6);
}

/* 矩阵网格 */
.ladder-grid {
  display: flex;
  flex-direction: column;
}

.ladder-row {
  display: flex;
  flex-direction: row;
  min-height: 48px;
}

/* 固定左侧列 */
.ladder-header-col {
  width: 95px;
  min-width: 95px;
  max-width: 95px;
  position: sticky;
  left: 0;
  z-index: 10;
  background: var(--bg-muted, #1a2333);
  border-color: var(--border-color, #2d3748) !important;
}

.corner-col {
  background: var(--bg-card, #1e2638) !important;
  z-index: 20;
}

.corner-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-main, #f1f5f9);
}

.corner-sub {
  font-size: 11px;
  color: var(--text-muted, #94a3b8);
  margin-top: 2px;
}

.tier-col {
  padding: 8px 4px;
}

.ban-number {
  font-size: 15px;
}

.latest-ban-count {
  font-size: 11px;
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
  padding: 1px 5px;
  border-radius: 10px;
  font-weight: 600;
}

.ban-total-hint {
  color: var(--text-muted, #94a3b8);
  margin-top: 4px;
  font-size: 11px;
}

/* 日期列 */
.ladder-day-col {
  flex: 1 0 155px;
  min-width: 155px;
  border-color: var(--border-color, #2d3748) !important;
}

.ladder-header-row {
  position: sticky;
  top: 0;
  z-index: 15;
  background: var(--bg-card, #1e2638);
}

.date-short {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-main, #f1f5f9);
}

.weekday-badge {
  font-size: 11px;
  padding: 1px 5px;
  border-radius: 4px;
  background: rgba(59, 130, 246, 0.15);
  color: var(--accent-color, #3b82f6);
  font-weight: 600;
}

.latest-tag {
  font-size: 10px;
  padding: 1px 4px;
  border-radius: 3px;
  background: #ef4444;
  color: #fff;
  font-weight: 700;
  animation: pulse-badge 2s infinite ease-in-out;
}

@keyframes pulse-badge {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.85; transform: scale(1.05); }
}

.dot-sep {
  margin: 0 4px;
}

.max-ban-text {
  color: #f97316;
  font-weight: 600;
}

.latest-col-header {
  background: rgba(59, 130, 246, 0.08);
  border-bottom: 2px solid var(--accent-color, #3b82f6) !important;
}

.latest-col-cell {
  background: rgba(59, 130, 246, 0.02);
}

.empty-cell {
  background: rgba(0, 0, 0, 0.03);
}

body.theme-dark .empty-cell {
  background: rgba(0, 0, 0, 0.15);
}

/* 股票卡片样式 */
.stock-item-card {
  padding: 5px 8px;
  border-radius: 6px;
  background: var(--bg-card, #1e2638);
  border: 1px solid var(--border-color, #334155);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.stock-item-card:hover {
  transform: translateY(-1px);
  border-color: var(--accent-color, #3b82f6);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.18);
}

/* 同股多日高亮脉冲效果 */
.stock-item-card.same-stock-highlight {
  border-color: #f59e0b !important;
  background: rgba(245, 158, 11, 0.18) !important;
  box-shadow: 0 0 10px rgba(245, 158, 11, 0.5) !important;
  transform: scale(1.03);
  z-index: 5;
  position: relative;
}

.stock-time {
  font-size: 11px;
  color: #f97316;
  font-family: monospace;
  font-weight: 600;
}

.stock-name {
  font-size: 13px;
  color: var(--text-main, #f1f5f9);
  letter-spacing: 0.3px;
}

/* 连板数标签 */
.stock-ban-pill {
  font-size: 10px;
  padding: 0 4px;
  border-radius: 3px;
  line-height: 15px;
  font-weight: 700;
  display: inline-block;
  white-space: nowrap;
}

.ban-pill-super {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: #ffffff;
  box-shadow: 0 1px 3px rgba(239, 68, 68, 0.4);
}

.ban-pill-high {
  background: rgba(249, 115, 22, 0.18);
  color: #f97316;
  border: 1px solid rgba(249, 115, 22, 0.35);
}

.ban-pill-mid {
  background: rgba(234, 179, 8, 0.15);
  color: #eab308;
  border: 1px solid rgba(234, 179, 8, 0.3);
}

.ban-pill-normal {
  background: rgba(56, 189, 248, 0.15);
  color: #38bdf8;
  border: 1px solid rgba(56, 189, 248, 0.3);
}

.circle-badge {
  font-size: 11px;
  line-height: 1;
}

.circle-c { color: #38bdf8; }
.circle-k { color: #f43f5e; }
.circle-b { color: #a855f7; }

.stock-real-rate {
  font-size: 11px;
  font-family: monospace;
}

/* 题材板块药丸 */
.stock-card-plates {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}

.plate-badge {
  font-size: 10px;
  padding: 1px 5px;
  border-radius: 4px;
  background: rgba(148, 163, 184, 0.12);
  color: var(--text-muted, #94a3b8);
  max-width: 80px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.plate-more {
  font-size: 10px;
  color: var(--text-muted, #94a3b8);
  align-self: center;
}

.stock-card-metrics {
  font-size: 11px;
  display: flex;
  flex-wrap: wrap;
}

.metric-item {
  color: var(--text-muted, #94a3b8);
}

.fd-item {
  color: #ef4444;
  font-weight: 500;
}
</style>
